// Static auth shim. sphinx has no identity provider — the operator is always
// signed in — but ~20 components still read WorkOS `useAuth()` for `user`,
// `signIn`, `getAccessToken`, … This keeps that call shape (a subset of
// authkit-react's ContextValue) so those call sites did not have to change;
// sign-in/out are no-ops and the access token is empty, which makes every
// bearer-resolution path fall through to "no token".
import { OPERATOR_USER } from "@/data/user";

export type AuthUser = {
  object: "user";
  id: string;
  email: string;
  emailVerified: boolean;
  profilePictureUrl: string | null;
  firstName: string | null;
  lastName: string | null;
  lastSignInAt: string | null;
  createdAt: string;
  updatedAt: string;
  externalId?: string | null;
  metadata?: Record<string, string>;
};

export type AuthContextValue = {
  isLoading: boolean;
  user: AuthUser | null;
  role: string | null;
  organizationId: string | null;
  permissions: string[];
  featureFlags: string[];
  impersonator: { email: string; reason: string | null } | null;
  signIn: (opts?: unknown) => Promise<void>;
  signUp: (opts?: unknown) => Promise<void>;
  getUser: () => Promise<AuthUser | null>;
  getAccessToken: (opts?: unknown) => Promise<string>;
  signOut: (opts?: unknown) => Promise<void>;
  switchToOrganization: (opts?: unknown) => Promise<void>;
};

export const OPERATOR_AUTH_USER: AuthUser = {
  object: "user",
  id: OPERATOR_USER.id,
  email: OPERATOR_USER.email,
  emailVerified: true,
  profilePictureUrl: null,
  firstName: OPERATOR_USER.name,
  lastName: null,
  lastSignInAt: null,
  createdAt: "1970-01-01T00:00:00.000Z",
  updatedAt: "1970-01-01T00:00:00.000Z",
};

const noop = async (_opts?: unknown): Promise<void> => {};

const AUTH: AuthContextValue = {
  isLoading: false,
  user: OPERATOR_AUTH_USER,
  role: "owner",
  organizationId: "default",
  permissions: [],
  featureFlags: [],
  impersonator: null,
  signIn: noop,
  signUp: noop,
  getUser: async () => OPERATOR_AUTH_USER,
  getAccessToken: async () => "",
  signOut: noop,
  switchToOrganization: noop,
};

export function useAuth(): AuthContextValue {
  return AUTH;
}
